import React, { useEffect, useMemo, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Chip,
} from "@material-ui/core";
import DeleteIcon from "@material-ui/icons/Delete";
import EditIcon from "@material-ui/icons/Edit";
import PublicIcon from "@material-ui/icons/Public";
import PageTitle from "../../components/PageTitle/PageTitle";
import StudentChargeMasterService from "./Locality/Service/studentChargeMasterService";
import SchoolService from "./Locality/Service/schoolService";

const getSchoolLabel = (row) => {
  const school = row?.schooleId;
  if (!school) return "—";
  if (typeof school === "object") {
    return school.schooleName || school.schoolAutoGenerateId || "—";
  }
  return String(school);
};

const getSchoolIdFromRow = (row) => {
  const school = row?.schooleId;
  if (!school) return "";
  if (typeof school === "object" && school._id) return String(school._id);
  return String(school);
};

const normalizeBillingType = (value) => {
  const type = String(value || "PER_STUDENT").toUpperCase();
  return type === "FIXED_AMOUNT" ? "FIXED_AMOUNT" : "PER_STUDENT";
};

const formatChargeAmount = (row) => {
  const billingType = normalizeBillingType(row?.billingType);
  if (billingType === "FIXED_AMOUNT") {
    return `₹${Number(row?.fixedAmount || 0).toLocaleString()} (fixed)`;
  }
  return `₹${Number(row?.amountPerStudent || 0).toLocaleString()} / student`;
};

export default function StudentChargeMaster() {
  const userDetails = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("userDetail") || "{}");
    } catch {
      return {};
    }
  }, []);

  const isSuperAdmin = String(userDetails?.role || "").toUpperCase() === "SUPERADMIN";

  const [globalDefault, setGlobalDefault] = useState(null);
  const [schoolOverrides, setSchoolOverrides] = useState([]);
  const [defaultAmountPerStudent, setDefaultAmountPerStudent] = useState(500);
  const [schoolList, setSchoolList] = useState([]);
  const [open, setOpen] = useState(false);
  const [globalOpen, setGlobalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [globalSaving, setGlobalSaving] = useState(false);
  const [loading, setLoading] = useState(false);

  const schoolsWithOverride = useMemo(
    () => new Set(schoolOverrides.map((r) => getSchoolIdFromRow(r)).filter(Boolean)),
    [schoolOverrides],
  );

  const schoolValidationSchema = useMemo(
    () =>
      Yup.object({
        schooleId: Yup.string().required("School is required"),
        billingType: Yup.string().oneOf(["PER_STUDENT", "FIXED_AMOUNT"]).required("Billing type is required"),
        amountPerStudent: Yup.number().when("billingType", {
          is: "PER_STUDENT",
          then: (schema) =>
            schema.typeError("Amount is required").positive("Amount must be > 0").required("Amount is required"),
          otherwise: (schema) => schema.nullable(),
        }),
        fixedAmount: Yup.number().when("billingType", {
          is: "FIXED_AMOUNT",
          then: (schema) =>
            schema.typeError("Amount is required").positive("Amount must be > 0").required("Amount is required"),
          otherwise: (schema) => schema.nullable(),
        }),
        status: Yup.string().oneOf(["Active", "Inactive"]).required("Status is required"),
      }),
    [],
  );

  const globalValidationSchema = useMemo(
    () =>
      Yup.object({
        billingType: Yup.string().oneOf(["PER_STUDENT", "FIXED_AMOUNT"]).required("Billing type is required"),
        amountPerStudent: Yup.number().when("billingType", {
          is: "PER_STUDENT",
          then: (schema) =>
            schema.typeError("Amount is required").positive("Amount must be > 0").required("Amount is required"),
          otherwise: (schema) => schema.nullable(),
        }),
        fixedAmount: Yup.number().when("billingType", {
          is: "FIXED_AMOUNT",
          then: (schema) =>
            schema.typeError("Amount is required").positive("Amount must be > 0").required("Amount is required"),
          otherwise: (schema) => schema.nullable(),
        }),
        status: Yup.string().oneOf(["Active", "Inactive"]).required("Status is required"),
      }),
    [],
  );

  const formik = useFormik({
    initialValues: {
      _id: "",
      schooleId: "",
      billingType: "PER_STUDENT",
      amountPerStudent: "",
      fixedAmount: "",
      status: "Active",
    },
    validationSchema: schoolValidationSchema,
    onSubmit: async (values) => {
      const billingType = normalizeBillingType(values.billingType);
      const payload = {
        _id: values._id || undefined,
        billingType,
        status: values.status,
      };
      if (billingType === "PER_STUDENT") {
        const amt = Number(values.amountPerStudent);
        if (!Number.isFinite(amt) || amt <= 0) return;
        payload.amountPerStudent = amt;
      } else {
        const amt = Number(values.fixedAmount);
        if (!Number.isFinite(amt) || amt <= 0) return;
        payload.fixedAmount = amt;
      }
      setSaving(true);
      try {
        await StudentChargeMasterService.upsertSchool(payload, values.schooleId);
        setOpen(false);
        await load();
      } finally {
        setSaving(false);
      }
    },
  });

  const availableSchools = useMemo(
    () =>
      schoolList.filter((s) => {
        const id = String(s._id);
        if (formik.values._id) return true;
        return !schoolsWithOverride.has(id);
      }),
    [schoolList, schoolsWithOverride, formik.values._id],
  );

  const globalFormik = useFormik({
    initialValues: {
      _id: "",
      billingType: "PER_STUDENT",
      amountPerStudent: "",
      fixedAmount: "",
      status: "Active",
    },
    validationSchema: globalValidationSchema,
    onSubmit: async (values) => {
      const billingType = normalizeBillingType(values.billingType);
      const payload = {
        _id: values._id || undefined,
        billingType,
        status: values.status,
      };
      if (billingType === "PER_STUDENT") {
        const amt = Number(values.amountPerStudent);
        if (!Number.isFinite(amt) || amt <= 0) return;
        payload.amountPerStudent = amt;
      } else {
        const amt = Number(values.fixedAmount);
        if (!Number.isFinite(amt) || amt <= 0) return;
        payload.fixedAmount = amt;
      }
      setGlobalSaving(true);
      try {
        await StudentChargeMasterService.upsertGlobal(payload);
        setGlobalOpen(false);
        await load();
      } finally {
        setGlobalSaving(false);
      }
    },
  });

  const loadSchools = async () => {
    try {
      const res = await SchoolService.getAllSchoolList();
      setSchoolList(Array.isArray(res) ? res : []);
    } catch {
      setSchoolList([]);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const res = await StudentChargeMasterService.list();
      const data = res?.data || res;
      setGlobalDefault(data?.globalDefault || null);
      setSchoolOverrides(Array.isArray(data?.schoolOverrides) ? data.schoolOverrides : []);
      if (data?.defaultAmountPerStudent != null) {
        setDefaultAmountPerStudent(Number(data.defaultAmountPerStudent) || 500);
      }
    } catch {
      setGlobalDefault(null);
      setSchoolOverrides([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isSuperAdmin) return;
    loadSchools().catch(() => {});
    load().catch(() => {});
  }, [isSuperAdmin]);

  const effectiveGlobalAmount = useMemo(() => {
    if (globalDefault && globalDefault.status === "Active") {
      const billingType = normalizeBillingType(globalDefault.billingType);
      if (billingType === "FIXED_AMOUNT") {
        return formatChargeAmount(globalDefault);
      }
      return `₹${Number(globalDefault.amountPerStudent) || defaultAmountPerStudent} / student`;
    }
    return `₹${defaultAmountPerStudent} / student`;
  }, [globalDefault, defaultAmountPerStudent]);

  const openGlobalEdit = () => {
    const billingType = normalizeBillingType(globalDefault?.billingType);
    globalFormik.setValues({
      _id: globalDefault?._id || "",
      billingType,
      amountPerStudent:
        billingType === "PER_STUDENT"
          ? String(globalDefault?.amountPerStudent ?? defaultAmountPerStudent)
          : "",
      fixedAmount: billingType === "FIXED_AMOUNT" ? String(globalDefault?.fixedAmount ?? "") : "",
      status: String(globalDefault?.status || "Active"),
    });
    setGlobalOpen(true);
  };

  const openSchoolNew = () => {
    formik.resetForm();
    formik.setValues({
      _id: "",
      schooleId: "",
      billingType: "PER_STUDENT",
      amountPerStudent: "",
      fixedAmount: "",
      status: "Active",
    });
    setOpen(true);
  };

  const openSchoolEdit = (row) => {
    const billingType = normalizeBillingType(row?.billingType);
    formik.setValues({
      _id: row?._id || "",
      schooleId: getSchoolIdFromRow(row),
      billingType,
      amountPerStudent: billingType === "PER_STUDENT" ? String(row?.amountPerStudent ?? "") : "",
      fixedAmount: billingType === "FIXED_AMOUNT" ? String(row?.fixedAmount ?? "") : "",
      status: String(row?.status || "Active"),
    });
    setOpen(true);
  };

  const onDeleteGlobal = async () => {
    if (!globalDefault?._id) return;
    const ok = window.confirm("Delete the global default fee? Schools without an override will use ₹500.");
    if (!ok) return;
    await StudentChargeMasterService.delete(globalDefault._id);
    await load();
  };

  const onDeleteSchool = async (row) => {
    if (!row?._id) return;
    const ok = window.confirm("Delete this school-specific fee? The school will use the global default.");
    if (!ok) return;
    await StudentChargeMasterService.delete(row._id);
    await load();
  };

  if (!isSuperAdmin) {
    return (
      <Box p={3}>
        <Typography>You don&apos;t have access to this page.</Typography>
      </Box>
    );
  }

  return (
    <Box p={3}>
      <PageTitle title="Student Charge Master" />

      <Card style={{ marginTop: 14, maxWidth: 980 }}>
        <CardContent>
          <Box display="flex" flexDirection="column" gridGap={12}>
            <Box display="flex" alignItems="center" gridGap={8}>
              <PublicIcon style={{ color: "#1769d6", fontSize: 22 }} />
              <Typography style={{ fontWeight: 700, color: "#0f2d4d", fontSize: 16 }}>
                Default fee (all schools)
              </Typography>
            </Box>
            <Typography variant="body2" style={{ color: "#6b7280" }}>
              Applies to every school unless a school-specific fee is set. If no global fee is saved, ₹
              {defaultAmountPerStudent} is used.
            </Typography>

            <Box
              display="flex"
              alignItems="center"
              flexWrap="wrap"
              gridGap={12}
              p={2}
              style={{
                background: "rgba(23,105,214,0.06)",
                borderRadius: 10,
                border: "1px solid rgba(23,105,214,0.15)",
              }}
            >
              <Typography style={{ fontWeight: 700, fontSize: 18 }}>{effectiveGlobalAmount}</Typography>
              {globalDefault ? (
                <Chip
                  size="small"
                  label={globalDefault.status}
                  style={{
                    backgroundColor: globalDefault.status === "Active" ? "#e6f7ed" : "#f4f6fa",
                    color: globalDefault.status === "Active" ? "#1a9b54" : "#9aa6b6",
                    fontWeight: 600,
                  }}
                />
              ) : (
                <Chip size="small" label="System default (₹500)" style={{ fontWeight: 600 }} />
              )}
              <Box flex={1} />
              <Button
                variant="outlined"
                color="primary"
                onClick={openGlobalEdit}
                style={{ textTransform: "none" }}
              >
                {globalDefault ? "Edit default" : "Set default fee"}
              </Button>
              {globalDefault?._id ? (
                <DeleteIcon
                  style={{ cursor: "pointer", color: "#dc2626" }}
                  onClick={onDeleteGlobal}
                  titleAccess="Delete global default"
                />
              ) : null}
            </Box>
          </Box>
        </CardContent>
      </Card>

      <Card style={{ marginTop: 16, maxWidth: 980 }}>
        <CardContent>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
            <Box>
              <Typography style={{ fontWeight: 700, color: "#0f2d4d", fontSize: 16 }}>
                School-specific fees
              </Typography>
              <Typography variant="body2" style={{ color: "#6b7280", marginTop: 4 }}>
                One override per school. Only listed schools use a custom amount; others use the default above.
              </Typography>
            </Box>
            <Button
              variant="contained"
              color="primary"
              onClick={openSchoolNew}
              disabled={availableSchools.length === 0 && !formik.values._id}
              style={{ textTransform: "none" }}
            >
              Add school fee
            </Button>
          </Box>

          <Divider style={{ marginBottom: 12 }} />

          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>School</TableCell>
                <TableCell>Billing type</TableCell>
                <TableCell>Amount</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Updated</TableCell>
                <TableCell align="right">Edit</TableCell>
                <TableCell align="right">Delete</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} style={{ color: "#6b7280" }}>
                    Loading…
                  </TableCell>
                </TableRow>
              ) : (
                schoolOverrides.map((row) => (
                  <TableRow
                    key={row._id}
                    style={row.status === "Active" ? { background: "rgba(34,197,94,0.06)" } : null}
                  >
                    <TableCell>{getSchoolLabel(row)}</TableCell>
                    <TableCell>
                      {normalizeBillingType(row?.billingType) === "FIXED_AMOUNT"
                        ? "Fixed amount"
                        : "Per student"}
                    </TableCell>
                    <TableCell>{formatChargeAmount(row)}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={row.status}
                        style={{
                          backgroundColor: row.status === "Active" ? "#e6f7ed" : "#f4f6fa",
                          color: row.status === "Active" ? "#1a9b54" : "#9aa6b6",
                          fontWeight: 600,
                        }}
                      />
                    </TableCell>
                    <TableCell>{row.updatedAt ? new Date(row.updatedAt).toLocaleString() : "-"}</TableCell>
                    <TableCell align="right">
                      <EditIcon style={{ cursor: "pointer" }} onClick={() => openSchoolEdit(row)} />
                    </TableCell>
                    <TableCell align="right">
                      <DeleteIcon style={{ cursor: "pointer" }} onClick={() => onDeleteSchool(row)} />
                    </TableCell>
                  </TableRow>
                ))
              )}
              {!loading && schoolOverrides.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} style={{ color: "#6b7280" }}>
                    No school-specific fees. All schools use the default fee above.
                  </TableCell>
                </TableRow>
              ) : null}
            </TableBody>
          </Table>

          <Typography variant="body2" style={{ color: "#6b7280", marginTop: 14 }}>
            Upgrade Plan amount uses either (students × fee per student) or a fixed amount, based on the
            billing type set above.
          </Typography>
        </CardContent>
      </Card>

      <Dialog open={globalOpen} onClose={() => (globalSaving ? null : setGlobalOpen(false))} fullWidth maxWidth="xs">
        <DialogTitle>{globalDefault ? "Edit default fee" : "Set default fee"}</DialogTitle>
        <DialogContent dividers>
          <Box display="flex" flexDirection="column" gridGap={12}>
            <FormControl variant="outlined">
              <InputLabel id="scm-global-billing-type-label">Billing type</InputLabel>
              <Select
                labelId="scm-global-billing-type-label"
                value={globalFormik.values.billingType}
                onChange={(e) => {
                  const nextType = String(e?.target?.value ?? "PER_STUDENT");
                  globalFormik.setFieldValue("billingType", nextType);
                  if (nextType === "PER_STUDENT") {
                    globalFormik.setFieldValue("fixedAmount", "");
                  } else {
                    globalFormik.setFieldValue("amountPerStudent", "");
                  }
                }}
                label="Billing type"
              >
                <MenuItem value="PER_STUDENT">Amount per student</MenuItem>
                <MenuItem value="FIXED_AMOUNT">Fixed amount</MenuItem>
              </Select>
            </FormControl>
            {normalizeBillingType(globalFormik.values.billingType) === "PER_STUDENT" ? (
              <TextField
                variant="outlined"
                label="Amount per student (all schools)"
                name="amountPerStudent"
                type="text"
                value={globalFormik.values.amountPerStudent}
                onChange={(e) => {
                  const digitsOnly = String(e?.target?.value ?? "").replace(/[^\d]/g, "");
                  globalFormik.setFieldValue("amountPerStudent", digitsOnly);
                }}
                onBlur={globalFormik.handleBlur}
                error={globalFormik.touched.amountPerStudent && Boolean(globalFormik.errors.amountPerStudent)}
                helperText={globalFormik.touched.amountPerStudent && globalFormik.errors.amountPerStudent}
                inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
                fullWidth
              />
            ) : (
              <TextField
                variant="outlined"
                label="Fixed amount (all schools)"
                name="fixedAmount"
                type="text"
                value={globalFormik.values.fixedAmount}
                onChange={(e) => {
                  const digitsOnly = String(e?.target?.value ?? "").replace(/[^\d]/g, "");
                  globalFormik.setFieldValue("fixedAmount", digitsOnly);
                }}
                onBlur={globalFormik.handleBlur}
                error={globalFormik.touched.fixedAmount && Boolean(globalFormik.errors.fixedAmount)}
                helperText={globalFormik.touched.fixedAmount && globalFormik.errors.fixedAmount}
                inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
                fullWidth
              />
            )}
            <FormControl variant="outlined">
              <InputLabel id="scm-global-status-label">Status</InputLabel>
              <Select
                labelId="scm-global-status-label"
                value={globalFormik.values.status}
                onChange={(e) => globalFormik.setFieldValue("status", String(e?.target?.value ?? "Active"))}
                label="Status"
              >
                <MenuItem value="Active">Active</MenuItem>
                <MenuItem value="Inactive">Inactive</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGlobalOpen(false)} disabled={globalSaving}>
            Cancel
          </Button>
          <Button color="primary" variant="contained" onClick={globalFormik.handleSubmit} disabled={globalSaving}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={open} onClose={() => (saving ? null : setOpen(false))} fullWidth maxWidth="xs">
        <DialogTitle>{formik.values._id ? "Edit school fee" : "Add school fee"}</DialogTitle>
        <DialogContent dividers>
          <Box display="flex" flexDirection="column" gridGap={12}>
            <FormControl variant="outlined" error={formik.touched.schooleId && Boolean(formik.errors.schooleId)}>
              <InputLabel id="scm-form-school-label">School</InputLabel>
              <Select
                labelId="scm-form-school-label"
                name="schooleId"
                label="School"
                value={formik.values.schooleId}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                disabled={Boolean(formik.values._id)}
              >
                {(formik.values._id
                  ? schoolList.filter((s) => String(s._id) === String(formik.values.schooleId))
                  : availableSchools
                ).map((school) => (
                  <MenuItem key={school._id} value={school._id}>
                    {school.schooleName || school.schoolAutoGenerateId || school._id}
                  </MenuItem>
                ))}
              </Select>
              {formik.touched.schooleId && formik.errors.schooleId ? (
                <Typography variant="caption" color="error">
                  {formik.errors.schooleId}
                </Typography>
              ) : null}
            </FormControl>
            <FormControl variant="outlined">
              <InputLabel id="scm-form-billing-type-label">Billing type</InputLabel>
              <Select
                labelId="scm-form-billing-type-label"
                value={formik.values.billingType}
                onChange={(e) => {
                  const nextType = String(e?.target?.value ?? "PER_STUDENT");
                  formik.setFieldValue("billingType", nextType);
                  if (nextType === "PER_STUDENT") {
                    formik.setFieldValue("fixedAmount", "");
                  } else {
                    formik.setFieldValue("amountPerStudent", "");
                  }
                }}
                label="Billing type"
              >
                <MenuItem value="PER_STUDENT">Amount per student</MenuItem>
                <MenuItem value="FIXED_AMOUNT">Fixed amount</MenuItem>
              </Select>
            </FormControl>
            {normalizeBillingType(formik.values.billingType) === "PER_STUDENT" ? (
              <TextField
                variant="outlined"
                label="Amount per student"
                name="amountPerStudent"
                type="text"
                value={formik.values.amountPerStudent}
                onChange={(e) => {
                  const digitsOnly = String(e?.target?.value ?? "").replace(/[^\d]/g, "");
                  formik.setFieldValue("amountPerStudent", digitsOnly);
                }}
                onBlur={formik.handleBlur}
                error={formik.touched.amountPerStudent && Boolean(formik.errors.amountPerStudent)}
                helperText={formik.touched.amountPerStudent && formik.errors.amountPerStudent}
                inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
                fullWidth
              />
            ) : (
              <TextField
                variant="outlined"
                label="Fixed amount"
                name="fixedAmount"
                type="text"
                value={formik.values.fixedAmount}
                onChange={(e) => {
                  const digitsOnly = String(e?.target?.value ?? "").replace(/[^\d]/g, "");
                  formik.setFieldValue("fixedAmount", digitsOnly);
                }}
                onBlur={formik.handleBlur}
                error={formik.touched.fixedAmount && Boolean(formik.errors.fixedAmount)}
                helperText={formik.touched.fixedAmount && formik.errors.fixedAmount}
                inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
                fullWidth
              />
            )}
            <FormControl variant="outlined">
              <InputLabel id="scm-status-label">Status</InputLabel>
              <Select
                labelId="scm-status-label"
                value={formik.values.status}
                onChange={(e) => formik.setFieldValue("status", String(e?.target?.value ?? "Active"))}
                label="Status"
              >
                <MenuItem value="Active">Active</MenuItem>
                <MenuItem value="Inactive">Inactive</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button
            color="primary"
            variant="contained"
            onClick={formik.handleSubmit}
            disabled={
              saving ||
              !formik.isValid ||
              !formik.values.schooleId ||
              (normalizeBillingType(formik.values.billingType) === "PER_STUDENT"
                ? !formik.values.amountPerStudent || Number(formik.values.amountPerStudent) <= 0
                : !formik.values.fixedAmount || Number(formik.values.fixedAmount) <= 0)
            }
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
